'use client'

import { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react'
import { format, parseISO, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns'
import { useDebouncedCallback } from 'use-debounce'
import { useStmGroupsForTab, useStmWeeksWithStats } from '@/features/stm/hooks/use-stm-weeks'
import { useArLeadershipDates } from '@/features/volunteers/hooks/volunteers.hooks'
import { doesOverlap } from '@/features/stm/lib/date-utils'
import type { NonHomeownerUser, VolunteerTableRow } from '@/features/volunteers/types/volunteers.type'
import { WeekCard } from './WeekCard'
import { BlocksManagementDialog } from './BlocksManagementDialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { ChevronLeft, ChevronRight, Search, Filter, Ban, Plus, Tag, CheckCircle, X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { TRIP_TYPES, GROUP_STATUSES } from '@/features/stm/lib/types'
import type { WeekWithStats, TripType, GroupStatus, Group } from '@/features/stm/lib/types'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useRegion } from '@/shared/hooks/use-region'
import { buildRegionUrl } from '@/shared/lib/utils'
import {
  SEARCH_PARAMS_TAB_KEY,
  SEARCH_PARAMS_WEEK_START_KEY,
  VOLUNTEER_TAB_FILTER_SEARCH,
} from '@/features/volunteers/constants/volunteer-tab-links'
import { GroupsMonthCalendar } from '@/features/stm/components/GroupsMonthCalendar'
import { LayoutGrid, Calendar, Eye, EyeOff } from 'lucide-react'

/** Get last name for sorting (last token, or second-to-last if last is numeric like "5172") */
function getLastNameForSort(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  const last = parts[parts.length - 1] ?? ''
  if (/^\d+$/.test(last) && parts.length > 1) {
    return parts[parts.length - 2] ?? last
  }
  return last
}

/** Get AR Team names (Base Leaders & Admins) whose serveDates overlap with the week for the given region, sorted by last name */
function getArTeamNamesForWeek(
  users: Array<NonHomeownerUser | VolunteerTableRow>,
  weekStart: string,
  weekEnd: string,
  regionId: number | undefined | null
): string[] {
  const names = new Set<string>()
  for (const user of users) {
    const serveDates = user.volunteer?.serveDates
    if (!serveDates || !Array.isArray(serveDates)) continue
    for (const period of serveDates) {
      if (regionId != null && period.regionId !== regionId) continue
      if (doesOverlap(period.from, period.to, weekStart, weekEnd)) {
        names.add(user.fullname || 'Unknown')
        break
      }
    }
  }
  return [...names].sort((a, b) => {
    const lastA = getLastNameForSort(a)
    const lastB = getLastNameForSort(b)
    return lastA.localeCompare(lastB, undefined, { sensitivity: 'base' })
  })
}

function groupMatchesCalendarSearch(group: Group, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true
  return [
    group.group_name,
    group.church_org,
    group.project_id,
    group.contact_name,
    group.contact_email,
    group.leader_name,
    group.leader_email,
  ].some((value) => value?.toLowerCase().includes(normalizedQuery))
}

function getGroupMonthAnchor(group: Group): Date | null {
  const rawDate = group.start_date || group.end_date
  if (!rawDate) return null
  const parsed = parseISO(rawDate)
  if (isNaN(parsed.getTime())) return null
  return startOfMonth(parsed)
}

const STORAGE_KEY_SHOW_CURRENT_WEEK_CARD = 'reliefhub.volunteers.calendar.showCurrentWeekCard'
const STORAGE_KEY_CALENDAR_BODY_VIEW = 'reliefhub.volunteers.calendar.bodyView'

interface CalendarGridProps {
  isAdmin: boolean
  regionId: number | undefined | null
  onOpenAddGroup?: () => void
}

export function CalendarGrid({ isAdmin, regionId, onOpenAddGroup }: CalendarGridProps) {
  const { regionSlug } = useRegion()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const weekStartParam = searchParams.get('weekStart')
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (weekStartParam) {
      const parsed = parseISO(weekStartParam)
      if (!isNaN(parsed.getTime())) return startOfMonth(parsed)
    }
    return new Date()
  })
  const monthStartStr = useMemo(
    () => format(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1), 'yyyy-MM-dd'),
    [currentMonth]
  )
  /** First day of the viewed month (ISO date) — pass through to group links so Back restores this month */
  const calendarMonthAnchor = monthStartStr
  const { data: weeks, isLoading, error } = useStmWeeksWithStats(regionId, monthStartStr, 1)
  const { data: groupsForSearch } = useStmGroupsForTab(regionId, monthStartStr)
  const { data: arData } = useArLeadershipDates(regionId)
  const arUsers = (arData?.success && arData?.data?.users) ? arData.data.users : []
  const [blocksDialogOpen, setBlocksDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [tripTypeFilter, setTripTypeFilter] = useState<TripType | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<GroupStatus | 'all'>('all')
  const [calendarBodyView, setCalendarBodyView] = useState<'weekly' | 'monthly'>('monthly')
  /** When false, the Current Week summary card is hidden; heading + toggle stay visible. */
  const [showCurrentWeekCard, setShowCurrentWeekCard] = useState(true)
  /** After reading localStorage so we do not overwrite before load. */
  const [currentWeekCardPrefReady, setCurrentWeekCardPrefReady] = useState(false)

  useEffect(() => {
    setSearchQuery(searchParams.get('search') || '')
  }, [searchParams])

  const updateSearchParam = useDebouncedCallback((value: string) => {
    const params = new URLSearchParams(searchParams.toString())
    if (value && value.trim().length > 0) {
      params.set('search', value)
    } else {
      params.delete('search')
    }
    params.set('page', '1')
    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname, { scroll: false })
  }, 750)

  const handleOpenVolunteerSearch = () => {
    const search = searchQuery.trim()
    if (!search) return
    updateSearchParam.cancel()
    const params = {
      [SEARCH_PARAMS_TAB_KEY]: VOLUNTEER_TAB_FILTER_SEARCH,
      page: '1',
      search,
    }
    const href = regionSlug
      ? buildRegionUrl(regionSlug, '/volunteers', params)
      : `/volunteers?${new URLSearchParams(params).toString()}`
    router.push(href)
  }

  const clearSearchParam = () => {
    updateSearchParam.cancel()
    setSearchQuery('')
    const params = new URLSearchParams(searchParams.toString())
    params.delete('search')
    params.set('page', '1')
    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname, { scroll: false })
  }

  const clearCalendarFilters = () => {
    clearSearchParam()
    setTripTypeFilter('all')
    setStatusFilter('all')
    setCurrentMonth(startOfMonth(new Date()))
    if (calendarBodyView === 'weekly') {
      setWeeklyTodayScrollPending(true)
    } else {
      setMonthlyTodayScrollTrigger((n) => n + 1)
    }
  }

  const hasCalendarFilters =
    searchQuery.trim().length > 0 || tripTypeFilter !== 'all' || statusFilter !== 'all'

  /** Read saved preferences before the browser paints so we do not flash the wrong state. */
  useLayoutEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY_SHOW_CURRENT_WEEK_CARD)
      if (raw === 'false') setShowCurrentWeekCard(false)
      else if (raw === 'true') setShowCurrentWeekCard(true)
    } catch {
      /* ignore */
    }
    try {
      const rawView = window.localStorage.getItem(STORAGE_KEY_CALENDAR_BODY_VIEW)
      if (rawView === 'weekly') setCalendarBodyView('weekly')
      else if (rawView === 'monthly') setCalendarBodyView('monthly')
    } catch {
      /* ignore */
    }
    setCurrentWeekCardPrefReady(true)
  }, [])

  useEffect(() => {
    if (!currentWeekCardPrefReady) return
    try {
      window.localStorage.setItem(
        STORAGE_KEY_SHOW_CURRENT_WEEK_CARD,
        showCurrentWeekCard ? 'true' : 'false'
      )
    } catch {
      /* ignore */
    }
  }, [showCurrentWeekCard, currentWeekCardPrefReady])

  useEffect(() => {
    if (!currentWeekCardPrefReady) return
    try {
      window.localStorage.setItem(STORAGE_KEY_CALENDAR_BODY_VIEW, calendarBodyView)
    } catch {
      /* ignore */
    }
  }, [calendarBodyView, currentWeekCardPrefReady])

  const normalizedSearchQuery = searchQuery.trim().toLowerCase()

  /** Weeks that overlap the selected calendar month (inclusive ISO date strings; avoids timezone bugs with parseISO + isSameMonth). */
  const weeksInMonth = useMemo(() => {
    if (!weeks) return []
    const monthStartStr = format(startOfMonth(currentMonth), 'yyyy-MM-dd')
    const monthEndStr = format(endOfMonth(currentMonth), 'yyyy-MM-dd')
    return weeks.filter((w) => {
      const weekStart = (w.week_start ?? '').slice(0, 10)
      const weekEnd = (w.week_end ?? '').slice(0, 10)
      if (!/^\d{4}-\d{2}-\d{2}$/.test(weekStart) || !/^\d{4}-\d{2}-\d{2}$/.test(weekEnd)) return false
      return weekStart <= monthEndStr && weekEnd >= monthStartStr
    })
  }, [weeks, currentMonth])

  /** Apply search/type/status filters to a week's groups */
  const applyGroupFilters = useMemo(
    () => (w: WeekWithStats) => {
      let filteredGroups = w.groups
      if (normalizedSearchQuery) {
        filteredGroups = filteredGroups.filter((g) =>
          groupMatchesCalendarSearch(g, normalizedSearchQuery)
        )
      }
      if (tripTypeFilter !== 'all') {
        filteredGroups = filteredGroups.filter((g) => g.trip_type === tripTypeFilter)
      }
      if (statusFilter !== 'all') {
        filteredGroups = filteredGroups.filter((g) => g.status === statusFilter)
      }
      return { ...w, groups: filteredGroups }
    },
    [normalizedSearchQuery, tripTypeFilter, statusFilter]
  )

  const filteredWeeks = useMemo(() => {
    const filtered = weeksInMonth.map(applyGroupFilters)
    return filtered.sort((a, b) => a.week_start.localeCompare(b.week_start))
  }, [weeksInMonth, applyGroupFilters])

  /** Week that contains today, if it appears in the selected month; hidden when viewing past/future months */
  const currentWeek = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    for (const w of filteredWeeks) {
      const weekStart = parseISO(w.week_start)
      const weekEnd = parseISO(w.week_end)
      weekStart.setHours(0, 0, 0, 0)
      weekEnd.setHours(23, 59, 59, 999)
      if (isWithinInterval(today, { start: weekStart, end: weekEnd })) {
        return w
      }
    }
    return null
  }, [filteredWeeks])

  const uniqueGroupsInMonth = useMemo(() => {
    const map = new Map<string, Group>()
    for (const w of weeksInMonth) {
      for (const g of w.groups) {
        map.set(g.id, g)
      }
    }
    return [...map.values()]
  }, [weeksInMonth])

  useEffect(() => {
    if (!normalizedSearchQuery || !groupsForSearch?.length) return
    const currentMonthHasMatch = uniqueGroupsInMonth.some((group) =>
      groupMatchesCalendarSearch(group, normalizedSearchQuery)
    )
    if (currentMonthHasMatch) return

    const firstMatchedGroup = groupsForSearch.find((group) =>
      groupMatchesCalendarSearch(group, normalizedSearchQuery)
    )
    if (!firstMatchedGroup) return

    const matchedMonth = getGroupMonthAnchor(firstMatchedGroup)
    if (!matchedMonth) return

    const currentMonthKey = format(startOfMonth(currentMonth), 'yyyy-MM-dd')
    const matchedMonthKey = format(matchedMonth, 'yyyy-MM-dd')
    if (matchedMonthKey !== currentMonthKey) {
      setCurrentMonth(matchedMonth)
    }
  }, [currentMonth, groupsForSearch, normalizedSearchQuery, uniqueGroupsInMonth])

  const filteredGroupsForMonthView = useMemo(() => {
    let list = uniqueGroupsInMonth
    if (normalizedSearchQuery) {
      list = list.filter((g) => groupMatchesCalendarSearch(g, normalizedSearchQuery))
    }
    if (tripTypeFilter !== 'all') {
      list = list.filter((g) => g.trip_type === tripTypeFilter)
    }
    if (statusFilter !== 'all') {
      list = list.filter((g) => g.status === statusFilter)
    }
    return list
  }, [uniqueGroupsInMonth, normalizedSearchQuery, tripTypeFilter, statusFilter])
  const visibleGroupCount = filteredGroupsForMonthView.length

  const groupDetailHrefForMonth = (groupId: string) => {
    const groupQuery: Record<string, string> = { from: 'calendar' }
    if (calendarMonthAnchor && /^\d{4}-\d{2}-\d{2}$/.test(calendarMonthAnchor)) {
      groupQuery[SEARCH_PARAMS_WEEK_START_KEY] = calendarMonthAnchor
    }
    return regionSlug
      ? buildRegionUrl(regionSlug, `/volunteers/groups/${groupId}`, groupQuery)
      : `/volunteers/groups/${groupId}?${new URLSearchParams(groupQuery).toString()}`
  }

  const handlePrevMonth = () => {
    setCurrentMonth((p) => new Date(p.getFullYear(), p.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setCurrentMonth((p) => new Date(p.getFullYear(), p.getMonth() + 1, 1))
  }

  /** Bump to ask `GroupsMonthCalendar` to scroll the month grid to the week containing local today */
  const [monthlyTodayScrollTrigger, setMonthlyTodayScrollTrigger] = useState(0)
  /** Weekly list: scroll to the week card that contains local today */
  const [weeklyTodayScrollPending, setWeeklyTodayScrollPending] = useState(false)
  const weeklyTodayRef = useRef<HTMLDivElement | null>(null)

  const handleToday = () => {
    const monthStart = startOfMonth(new Date())
    setCurrentMonth(monthStart)
    if (calendarBodyView === 'weekly') {
      setWeeklyTodayScrollPending(true)
    } else {
      setMonthlyTodayScrollTrigger((n) => n + 1)
    }
  }

  useEffect(() => {
    if (!weeklyTodayScrollPending) return
    if (isLoading) return
    if (!currentWeek) {
      setWeeklyTodayScrollPending(false)
      return
    }
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        weeklyTodayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        setWeeklyTodayScrollPending(false)
      })
    })
    return () => cancelAnimationFrame(id)
  }, [weeklyTodayScrollPending, currentWeek, isLoading])

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-destructive">Failed to load calendar data. Please try again.</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-row flex-nowrap items-center gap-1 sm:gap-4 overflow-x-auto pb-0.5 w-full sm:flex-wrap sm:overflow-visible sm:pb-0 lg:justify-between">
        <div className="flex items-center gap-1 sm:gap-3 shrink-0 min-w-0">
          <div className="flex items-center gap-0 sm:gap-0.5 md:gap-1 bg-muted/50 rounded-lg sm:rounded-xl p-0 sm:p-0.5 md:p-1 shrink-0">
            <Button type="button" variant="ghost" size="icon" onClick={handlePrevMonth} className="rounded-md sm:rounded-lg h-7 w-7 sm:h-9 sm:w-9 shrink-0" aria-label="Previous month">
              <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
            <h2 className="text-[11px] leading-tight sm:text-base lg:text-lg font-bold min-w-0 tabular-nums sm:min-w-[140px] lg:min-w-[160px] text-center px-0.5 sm:px-2">
              <span className="sm:hidden">{format(currentMonth, 'MMM yyyy')}</span>
              <span className="hidden sm:inline">{format(currentMonth, 'MMMM yyyy')}</span>
            </h2>
            <Button type="button" variant="ghost" size="icon" onClick={handleNextMonth} className="rounded-md sm:rounded-lg h-7 w-7 sm:h-9 sm:w-9 shrink-0" aria-label="Next month">
              <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleToday}
            className="rounded-lg font-medium h-7 min-h-0 px-1.5 py-0 text-[10px] leading-none sm:h-9 sm:min-h-9 sm:px-3 sm:text-sm sm:leading-normal shrink-0 bg-background/50 sm:bg-background"
          >
            Today
          </Button>
          {isAdmin && (
            <div className="hidden sm:flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBlocksDialogOpen(true)}
                className="gap-1.5 sm:gap-2 rounded-lg font-medium"
              >
                <Ban className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Manage Blocks</span>
                <span className="sm:hidden">Blocks</span>
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-0.5 sm:gap-2 flex-nowrap min-w-0 shrink-0 sm:flex-1 sm:min-w-[200px]">
          {/* Desktop: full search input */}
          <div className="relative flex-1 min-w-[220px] hidden sm:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search volunteers, groups, dates..."
              value={searchQuery}
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value
                setSearchQuery(value)
                updateSearchParam(value)
              }}
              className="pl-9 rounded-lg bg-background/50 h-10"
            />
          </div>
          {searchQuery.trim().length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleOpenVolunteerSearch}
              className="hidden sm:inline-flex h-10 shrink-0 rounded-lg whitespace-nowrap"
            >
              Search volunteers
            </Button>
          )}
          {/* Mobile: search icon + popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="sm:hidden h-7 w-7 rounded-lg bg-background/50 shrink-0"
                aria-label="Search groups"
              >
                <Search className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[calc(100vw-2rem)] max-w-sm p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search volunteers, groups, dates..."
                  value={searchQuery}
                  onChange={(e) => {
                    const value = (e.target as HTMLInputElement).value
                    setSearchQuery(value)
                    updateSearchParam(value)
                  }}
                  className="pl-9 rounded-lg h-10"
                  autoFocus
                />
              </div>
              {searchQuery.trim().length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleOpenVolunteerSearch}
                  className="mt-3 w-full"
                >
                  Search volunteers
                </Button>
              )}
            </PopoverContent>
          </Popover>
          <Select value={tripTypeFilter} onValueChange={(v) => setTripTypeFilter(v as TripType | 'all')}>
            <SelectTrigger className="w-7 h-7 sm:w-[150px] sm:h-10 rounded-lg bg-background/50 px-1.5 sm:px-3 shrink-0" title="All Types">
              <Tag className="h-3.5 w-3.5 sm:hidden shrink-0" aria-hidden />
              <span className="hidden sm:inline"><SelectValue placeholder="Trip Type" /></span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {TRIP_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as GroupStatus | 'all')}>
            <SelectTrigger className="w-7 h-7 sm:w-[140px] sm:h-10 rounded-lg bg-background/50 px-1.5 sm:px-3 shrink-0" title="All Status">
              <CheckCircle className="h-3.5 w-3.5 sm:hidden shrink-0" aria-hidden />
              <span className="hidden sm:inline"><SelectValue placeholder="Status" /></span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {GROUP_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {onOpenAddGroup && (
            <Button
              variant="default"
              size="sm"
              onClick={onOpenAddGroup}
              className="bg-primary hover:bg-primary/90 text-white font-bold rounded-md gap-1 sm:gap-2 py-1 h-7 w-7 sm:h-10 sm:w-auto sm:px-4 flex-shrink-0"
              aria-label="Add Group"
            >
              <Plus className="w-3.5 h-3.5 sm:w-5 sm:h-5 shrink-0" />
              <span className="hidden sm:inline">Add Group</span>
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-muted-foreground">
          <span className="font-bold">
            Showing {visibleGroupCount.toLocaleString()} group{visibleGroupCount === 1 ? '' : 's'}
          </span>
        </div>
        {hasCalendarFilters && (
          <>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter className="w-4 h-4" />
            <span>Active filters:</span>
          </div>
          {searchQuery.trim().length > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm">
              <span className="font-medium">Search:</span>
              <span>{searchQuery.trim()}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto p-0 ml-1 hover:bg-primary/20"
                onClick={clearSearchParam}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          )}
          {tripTypeFilter !== 'all' && (
            <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm">
              <span className="font-medium">Type:</span>
              <span>{tripTypeFilter}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto p-0 ml-1 hover:bg-primary/20"
                onClick={() => setTripTypeFilter('all')}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          )}
          {statusFilter !== 'all' && (
            <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm">
              <span className="font-medium">Status:</span>
              <span>{statusFilter}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto p-0 ml-1 hover:bg-primary/20"
                onClick={() => setStatusFilter('all')}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearCalendarFilters}
            className="text-xs"
          >
            Clear all
          </Button>
        </div>
          </>
        )}
      </div>

      <div className="flex flex-nowrap items-center gap-2 sm:gap-4 px-1 text-xs text-muted-foreground whitespace-nowrap overflow-x-auto">
        <span className="font-medium text-foreground">Capacity:</span>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-400 to-green-500" />
          <span>&lt;70%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500" />
          <span>70-90%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-gradient-to-r from-rose-400 to-red-500" />
          <span>&gt;90%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-gradient-to-r from-slate-400 to-gray-500" />
          <span>Blocked</span>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-[200px] w-full rounded-xl" />
          ))}
        </div>
      ) : filteredWeeks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
            <Filter className="h-8 w-8 opacity-40" />
          </div>
          <p className="font-medium">No weeks found</p>
          <p className="text-sm mt-1">{format(currentMonth, 'MMMM yyyy')}</p>
        </div>
      ) : (
        <div className="space-y-8">
          {currentWeek && (
            <section aria-label="Current week">
              <div className="mb-3 flex flex-row flex-wrap items-center justify-between gap-2 sm:gap-3">
                <h3 className="text-base font-bold text-foreground tracking-tight flex min-w-0 flex-1 items-center gap-2">
                  <span className="w-1 h-5 shrink-0 rounded-full bg-primary" aria-hidden />
                  Current Week
                </h3>
                <div
                  className="inline-flex w-auto max-w-full shrink-0 items-center gap-px rounded-full border border-border/80 bg-muted/40 p-px shadow-sm"
                  role="group"
                  aria-label="Show or hide current week summary card"
                >
                  <Button
                    type="button"
                    variant={showCurrentWeekCard ? 'default' : 'ghost'}
                    size="sm"
                    className="h-6 min-h-6 shrink-0 gap-0.5 rounded-full px-1.5 text-[10px] font-medium leading-none sm:h-7 sm:min-h-7 sm:gap-1 sm:px-2.5 sm:text-xs [&_svg]:size-3 sm:[&_svg]:size-3.5"
                    onClick={() => setShowCurrentWeekCard(true)}
                    aria-pressed={showCurrentWeekCard}
                    aria-label="Show current week summary"
                  >
                    <Eye className="shrink-0" aria-hidden />
                    <span className="hidden sm:inline">Show</span>
                  </Button>
                  <Button
                    type="button"
                    variant={!showCurrentWeekCard ? 'default' : 'ghost'}
                    size="sm"
                    className="h-6 min-h-6 shrink-0 gap-0.5 rounded-full px-1.5 text-[10px] font-medium leading-none sm:h-7 sm:min-h-7 sm:gap-1 sm:px-2.5 sm:text-xs [&_svg]:size-3 sm:[&_svg]:size-3.5"
                    onClick={() => setShowCurrentWeekCard(false)}
                    aria-pressed={!showCurrentWeekCard}
                    aria-label="Hide current week summary"
                  >
                    <EyeOff className="shrink-0" aria-hidden />
                    <span className="hidden sm:inline">Hide</span>
                  </Button>
                </div>
              </div>
              {showCurrentWeekCard && (
                <div className="space-y-4">
                  <WeekCard
                    key={currentWeek.id}
                    week={currentWeek}
                    onClick={() => {}}
                    isSelected={false}
                    calendarMonthAnchor={calendarMonthAnchor}
                    arTeamNames={getArTeamNamesForWeek(arUsers, currentWeek.week_start, currentWeek.week_end, regionId ?? undefined)}
                  />
                </div>
              )}
            </section>
          )}

          <section aria-label="Groups in this month" className="space-y-4">
            <h3 id="calendar-weeks-in-month-heading" className="sr-only">
              {calendarBodyView === 'weekly'
                ? `Weeks in ${format(currentMonth, 'MMMM yyyy')}`
                : `${format(currentMonth, 'MMMM yyyy')} calendar`}
            </h3>
            <div
              className={cn(
                'flex flex-row flex-wrap items-center gap-2 sm:gap-3',
                !currentWeek || calendarBodyView === 'monthly' ? 'justify-between' : 'justify-end'
              )}
            >
              {/* Near the grid: show month + Today when no "today" in month, or on Monthly view (even with Current Week). Hide only on Weekly when Current Week exists — top bar is enough. */}
              {(!currentWeek || calendarBodyView === 'monthly') && (
                <div
                  className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:gap-3"
                  role="navigation"
                  aria-label="Month for this section"
                >
                  <span className="h-8 w-1 shrink-0 self-center rounded-full bg-muted-foreground/60" aria-hidden />
                  <div className="flex min-w-0 flex-wrap items-center gap-0.5 rounded-lg border border-border bg-muted/30 p-0.5">
                    <div className="flex min-w-0 items-center gap-0.5 rounded-md bg-background/80 p-0.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={handlePrevMonth}
                        className="h-8 w-8 shrink-0 rounded-md"
                        aria-label="Previous month"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </Button>
                      <p className="min-w-[5.75rem] text-center text-xs font-bold tabular-nums text-foreground sm:min-w-[7.25rem]">
                        <span className="sm:hidden">{format(currentMonth, 'MMM yyyy')}</span>
                        <span className="hidden sm:inline">{format(currentMonth, 'MMMM yyyy')}</span>
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={handleNextMonth}
                        className="h-8 w-8 shrink-0 rounded-md"
                        aria-label="Next month"
                      >
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleToday}
                      className="h-8 shrink-0 rounded-md px-2 text-xs font-medium"
                    >
                      Today
                    </Button>
                  </div>
                </div>
              )}
              <div
                className="inline-flex w-auto max-w-full shrink-0 items-center gap-px rounded-full border border-border/80 bg-muted/40 p-px shadow-sm"
                role="group"
                aria-label="Monthly calendar or weekly cards"
              >
                <Button
                  type="button"
                  variant={calendarBodyView === 'monthly' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-6 min-h-6 shrink-0 gap-0.5 rounded-full px-1.5 text-[10px] font-medium leading-none sm:h-7 sm:min-h-7 sm:gap-1 sm:px-2.5 sm:text-xs [&_svg]:size-3 sm:[&_svg]:size-3.5"
                  onClick={() => setCalendarBodyView('monthly')}
                  aria-pressed={calendarBodyView === 'monthly'}
                  aria-label="Monthly calendar view"
                >
                  <Calendar className="shrink-0" aria-hidden />
                  <span className="hidden sm:inline">Monthly</span>
                </Button>
                <Button
                  type="button"
                  variant={calendarBodyView === 'weekly' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-6 min-h-6 shrink-0 gap-0.5 rounded-full px-1.5 text-[10px] font-medium leading-none sm:h-7 sm:min-h-7 sm:gap-1 sm:px-2.5 sm:text-xs [&_svg]:size-3 sm:[&_svg]:size-3.5"
                  onClick={() => setCalendarBodyView('weekly')}
                  aria-pressed={calendarBodyView === 'weekly'}
                  aria-label="Weekly cards view"
                >
                  <LayoutGrid className="shrink-0" aria-hidden />
                  <span className="hidden sm:inline">Weekly</span>
                </Button>
              </div>
            </div>

            {calendarBodyView === 'weekly' ? (
              <div className="space-y-4">
                {filteredWeeks.map((w) => {
                  const isCurrentCalendarWeek =
                    currentWeek != null && w.week_start === currentWeek.week_start && w.id === currentWeek.id
                  return (
                    <div
                      key={w.id}
                      ref={isCurrentCalendarWeek ? weeklyTodayRef : undefined}
                      role="article"
                      aria-current={isCurrentCalendarWeek ? 'true' : undefined}
                      className={cn(
                        isCurrentCalendarWeek &&
                          'rounded-xl border-2 border-emerald-300/70 bg-emerald-50/60 p-2 sm:p-2.5 transition-colors dark:border-emerald-700/50 dark:bg-emerald-950/25'
                      )}
                    >
                      {isCurrentCalendarWeek && (
                        <span className="sr-only">Current week</span>
                      )}
                      <WeekCard
                        week={w}
                        onClick={() => {}}
                        isSelected={false}
                        calendarRowTone={isCurrentCalendarWeek ? 'default' : 'muted'}
                        calendarMonthAnchor={calendarMonthAnchor}
                        arTeamNames={getArTeamNamesForWeek(arUsers, w.week_start, w.week_end, regionId ?? undefined)}
                      />
                    </div>
                  )
                })}
              </div>
            ) : (
              <GroupsMonthCalendar
                groups={filteredGroupsForMonthView}
                groupHref={groupDetailHrefForMonth}
                currentMonth={currentMonth}
                onCurrentMonthChange={setCurrentMonth}
                showMonthToolbar={false}
                weeksWithCapacity={weeksInMonth}
                arUsers={arUsers}
                regionId={regionId ?? undefined}
                scrollToTodayTrigger={monthlyTodayScrollTrigger}
              />
            )}
          </section>
        </div>
      )}

      <BlocksManagementDialog
        open={blocksDialogOpen}
        onOpenChange={setBlocksDialogOpen}
        isAdmin={isAdmin}
        regionId={regionId}
      />
    </div>
  )
}
